import { useCallback, useEffect, useMemo, useState } from 'react';
import { Title, useNotify } from 'react-admin';
import {
    Alert,
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    CircularProgress,
    Divider,
    FormControlLabel,
    Stack,
    Switch,
    TextField,
    Typography,
} from '@mui/material';

import {
    defaultPracticeSocialProofSettings,
    getPracticeSocialProofSettings,
    savePracticeSocialProofSettings,
    type PracticeSocialProofSettings,
    type PracticeSocialProofSettingsSnapshot,
} from './socialProofSettingsApi';

type NumericSettingKey = {
    [Key in keyof PracticeSocialProofSettings]: PracticeSocialProofSettings[Key] extends number ? Key : never;
}[keyof PracticeSocialProofSettings];

type NumericFieldDefinition = {
    key: NumericSettingKey;
    label: string;
    helperText: string;
    minimum: number;
    maximum: number;
};

const audienceFields: NumericFieldDefinition[] = [
    {
        key: 'minimumCompletedAttempts',
        label: 'Minimum completed practices',
        helperText: 'The current completed practice is included in this count.',
        minimum: 1,
        maximum: 100000,
    },
    {
        key: 'minimumAppExperienceRating',
        label: 'Minimum app rating (out of 5)',
        helperText: 'Set to 0 to ignore this eligibility signal. The internal testimonial form still requires a saved rating.',
        minimum: 0,
        maximum: 5,
    },
    {
        key: 'minimumCurrentScoreOutOf90',
        label: 'Minimum current score (out of 90)',
        helperText: 'Only finalized PTE Ultra reporting-scale scores are considered.',
        minimum: 10,
        maximum: 90,
    },
];

const improvementFields: NumericFieldDefinition[] = [
    {
        key: 'requiredImprovementSteps',
        label: 'Required continuous improvement steps',
        helperText: '2 means two previous same-task scores plus the current score.',
        minimum: 1,
        maximum: 10,
    },
    {
        key: 'minimumImprovementPerStepPoints',
        label: 'Minimum points gained per step',
        helperText: 'Each consecutive score must improve by at least this many points.',
        minimum: 0,
        maximum: 80,
    },
    {
        key: 'minimumTotalImprovementPoints',
        label: 'Minimum total improvement points',
        helperText: 'Difference between the oldest compared score and current score.',
        minimum: 0,
        maximum: 80,
    },
];

const frequencyFields: NumericFieldDefinition[] = [
    {
        key: 'promptCooldownDays',
        label: 'Prompt cooldown (days)',
        helperText: 'Set to 0 for no cooldown.',
        minimum: 0,
        maximum: 3650,
    },
    {
        key: 'maximumPromptsPerUser',
        label: 'Maximum prompts per user',
        helperText: 'Set to 0 to suppress every prompt while retaining the other settings.',
        minimum: 0,
        maximum: 100,
    },
];

function validate(settings: PracticeSocialProofSettings): string[] {
    const errors: string[] = [];
    const fields = [...audienceFields, ...improvementFields, ...frequencyFields];
    for (const field of fields) {
        const value = settings[field.key];
        if (!Number.isInteger(value) || value < field.minimum || value > field.maximum) {
            errors.push(`${field.label} must be a whole number between ${field.minimum} and ${field.maximum}.`);
        }
    }
    if (settings.minimumCompletedAttempts < settings.requiredImprovementSteps + 1) {
        errors.push('Minimum completed practices must include the configured history and the current attempt.');
    }
    if (settings.requiredImprovementSteps * settings.minimumImprovementPerStepPoints > 80) {
        errors.push('The required per-step improvement is impossible on the 10-90 scale.');
    }
    const headlineLength = settings.promptHeadline.trim().length;
    if (headlineLength < 1 || headlineLength > 100) {
        errors.push('Prompt headline must contain 1 to 100 characters.');
    }
    const messageLength = settings.promptMessage.trim().length;
    if (messageLength < 1 || messageLength > 300) {
        errors.push('Prompt message must contain 1 to 300 characters.');
    }
    return errors;
}

function NumericField({
    definition,
    value,
    onChange,
}: {
    definition: NumericFieldDefinition;
    value: number;
    onChange: (value: number) => void;
}) {
    return (
        <TextField
            label={definition.label}
            value={value}
            onChange={(event) => onChange(Number(event.target.value))}
            helperText={definition.helperText}
            type="number"
            fullWidth
            slotProps={{
                htmlInput: {
                    min: definition.minimum,
                    max: definition.maximum,
                    step: 1,
                },
            }}
        />
    );
}

function SettingsSection({
    title,
    description,
    children,
}: {
    title: string;
    description: string;
    children: React.ReactNode;
}) {
    return (
        <Stack spacing={2}>
            <Box>
                <Typography variant="h6" fontWeight={800}>
                    {title}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                    {description}
                </Typography>
            </Box>
            <Box
                sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' },
                    gap: 2,
                }}
            >
                {children}
            </Box>
        </Stack>
    );
}

export function SocialProofSettingsPage() {
    const notify = useNotify();
    const [snapshot, setSnapshot] = useState<PracticeSocialProofSettingsSnapshot | null>(null);
    const [draft, setDraft] = useState<PracticeSocialProofSettings>(defaultPracticeSocialProofSettings);
    const [isLoading, setIsLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);
    const [loadError, setLoadError] = useState('');
    const validationErrors = useMemo(() => validate(draft), [draft]);

    const load = useCallback(async () => {
        setIsLoading(true);
        setLoadError('');
        try {
            const loaded = await getPracticeSocialProofSettings();
            setSnapshot(loaded);
            setDraft(loaded.configuration);
        } catch (error) {
            setLoadError(error instanceof Error ? error.message : 'Unable to load social proof settings.');
        } finally {
            setIsLoading(false);
        }
    }, []);

    useEffect(() => {
        void load();
    }, [load]);

    const updateNumber = (key: NumericSettingKey, value: number) => {
        setDraft((current) => ({ ...current, [key]: value }));
    };

    const save = async () => {
        if (validationErrors.length > 0) return;
        setIsSaving(true);
        try {
            const saved = await savePracticeSocialProofSettings({
                ...draft,
                promptHeadline: draft.promptHeadline.trim(),
                promptMessage: draft.promptMessage.trim(),
            });
            setSnapshot(saved);
            setDraft(saved.configuration);
            notify('Social proof eligibility settings saved', { type: 'info' });
        } catch (error) {
            notify(error instanceof Error ? error.message : 'Unable to save social proof settings', { type: 'warning' });
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <Box sx={{ width: '100%', maxWidth: 1180, mx: 'auto' }}>
            <Title title="Social Proof Settings" />
            <Card sx={{ borderRadius: 4, boxShadow: '0 24px 80px rgba(15, 23, 42, 0.12)' }}>
                <CardContent sx={{ p: { xs: 2.5, md: 4 } }}>
                    {isLoading ? (
                        <Stack alignItems="center" py={8}>
                            <CircularProgress />
                        </Stack>
                    ) : (
                        <Stack spacing={3}>
                            <Box
                                sx={{
                                    display: 'flex',
                                    gap: 2,
                                    alignItems: { xs: 'flex-start', sm: 'center' },
                                    justifyContent: 'space-between',
                                    flexDirection: { xs: 'column', sm: 'row' },
                                }}
                            >
                                <Box>
                                    <Typography variant="h4" fontWeight={900}>
                                        Practice social-proof eligibility
                                    </Typography>
                                    <Typography color="text.secondary">
                                        Controls the happy-moment prompt shown after a standalone Academic or Core practice.
                                    </Typography>
                                </Box>
                                <Chip
                                    color={snapshot?.isPersisted ? 'success' : 'warning'}
                                    label={snapshot?.isPersisted ? 'Database-backed' : 'Using appsettings fallback'}
                                />
                            </Box>

                            {loadError ? (
                                <Alert
                                    severity="error"
                                    action={
                                        <Button color="inherit" onClick={() => void load()}>
                                            Retry
                                        </Button>
                                    }
                                >
                                    {loadError}
                                </Alert>
                            ) : null}

                            {snapshot?.validationErrors.length ? (
                                <Alert severity="error">{snapshot.validationErrors.join(' ')}</Alert>
                            ) : null}

                            <Alert severity="info">
                                Academic and Core attempt counts stay separate. Improvement is compared only across the same task
                                type, using finalized 10-90 reporting-scale scores. These settings do not change learner scoring.
                            </Alert>

                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={draft.enabled}
                                        onChange={(event) =>
                                            setDraft((current) => ({ ...current, enabled: event.target.checked }))
                                        }
                                    />
                                }
                                label={
                                    <Box>
                                        <Typography fontWeight={800}>Enable practice social-proof prompts</Typography>
                                        <Typography variant="body2" color="text.secondary">
                                            Turning this off stops new offers immediately without deleting prompt history.
                                        </Typography>
                                    </Box>
                                }
                            />

                            <Divider />
                            <SettingsSection
                                title="Eligible audience"
                                description="Require experienced, positively satisfied learners at a meaningful score level."
                            >
                                {audienceFields.map((field) => (
                                    <NumericField
                                        key={field.key}
                                        definition={field}
                                        value={draft[field.key]}
                                        onChange={(value) => updateNumber(field.key, value)}
                                    />
                                ))}
                            </SettingsSection>

                            <Divider />
                            <SettingsSection
                                title="Happy-moment improvement"
                                description="Define how much continuous same-task improvement is needed before prompting."
                            >
                                {improvementFields.map((field) => (
                                    <NumericField
                                        key={field.key}
                                        definition={field}
                                        value={draft[field.key]}
                                        onChange={(value) => updateNumber(field.key, value)}
                                    />
                                ))}
                            </SettingsSection>

                            <Divider />
                            <SettingsSection
                                title="Frequency controls"
                                description="Limit how often an otherwise eligible learner can see the prompt."
                            >
                                {frequencyFields.map((field) => (
                                    <NumericField
                                        key={field.key}
                                        definition={field}
                                        value={draft[field.key]}
                                        onChange={(value) => updateNumber(field.key, value)}
                                    />
                                ))}
                            </SettingsSection>

                            <Divider />
                            <SettingsSection
                                title="Prompt content"
                                description="Customize the copy shown before Trustpilot, testimonial, Facebook, Instagram and YouTube options."
                            >
                                <TextField
                                    label="Prompt headline"
                                    value={draft.promptHeadline}
                                    onChange={(event) =>
                                        setDraft((current) => ({ ...current, promptHeadline: event.target.value }))
                                    }
                                    helperText={`${draft.promptHeadline.trim().length}/100 characters`}
                                    slotProps={{ htmlInput: { maxLength: 100 } }}
                                    fullWidth
                                />
                                <TextField
                                    label="Prompt message"
                                    value={draft.promptMessage}
                                    onChange={(event) =>
                                        setDraft((current) => ({ ...current, promptMessage: event.target.value }))
                                    }
                                    helperText={`${draft.promptMessage.trim().length}/300 characters`}
                                    slotProps={{ htmlInput: { maxLength: 300 } }}
                                    multiline
                                    minRows={3}
                                    fullWidth
                                />
                            </SettingsSection>

                            {validationErrors.length > 0 ? (
                                <Alert severity="warning">{validationErrors.join(' ')}</Alert>
                            ) : null}

                            <Box
                                sx={{
                                    display: 'flex',
                                    gap: 1.5,
                                    justifyContent: 'flex-end',
                                    flexWrap: 'wrap',
                                }}
                            >
                                <Button variant="outlined" disabled={isSaving} onClick={() => void load()}>
                                    Reload
                                </Button>
                                <Button
                                    variant="contained"
                                    disabled={isSaving || validationErrors.length > 0 || Boolean(loadError)}
                                    onClick={() => void save()}
                                >
                                    {isSaving ? 'Saving…' : 'Save settings'}
                                </Button>
                            </Box>

                            {snapshot?.updatedAtUtc ? (
                                <Typography variant="caption" color="text.secondary" textAlign="right">
                                    Last saved: {new Date(snapshot.updatedAtUtc).toLocaleString()}
                                </Typography>
                            ) : null}
                        </Stack>
                    )}
                </CardContent>
            </Card>
        </Box>
    );
}
